import { Component, DestroyRef, ElementRef, computed, effect, inject, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, of, Subscription, switchMap } from 'rxjs';
import { SessionStore } from '../../../core/auth/session.store';
import { TranslationService } from '../../../core/i18n/translation.service';
import { ReportAuditOption, ReportHeader, ReportIteration, ReportSummary } from '../models/report.models';
import { ReportsApi } from '../services/reports.api';
import { ReportScoreCardComponent } from './report-score-card.component';
import { ReportTrendComponent } from './report-trend.component';
import { ReportAreasComponent } from './report-areas.component';
import { ReportStatusDistributionComponent } from './report-status-distribution.component';
import { ReportStatusKpisComponent } from './report-status-kpis.component';
import { ReportNarrativeComponent } from './report-narrative.component';
import { ReportAggregateComponent } from './report-aggregate.component';

@Component({
  selector: 'app-reports',
  imports: [ReportScoreCardComponent, ReportTrendComponent, ReportAreasComponent, ReportStatusDistributionComponent, ReportStatusKpisComponent, ReportNarrativeComponent, ReportAggregateComponent],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.css',
})
export class ReportsComponent {
  readonly reportIssueRequested = output<void>();
  protected readonly activeTab = signal<'locations' | 'aggregate'>('aggregate');
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly api = inject(ReportsApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly session = inject(SessionStore);
  protected readonly i18n = inject(TranslationService);
  protected readonly options = signal<ReportAuditOption[]>([]);
  protected readonly selectedKey = signal('');
  protected readonly range = signal('7');
  protected readonly rangeLoading = signal(false);
  protected readonly rangeUpdateError = signal('');
  private readonly completedCount = signal<number | null>(null);
  protected readonly rangeMax = computed(() => Math.max(1, Math.min(100, this.completedCount() ?? 100)));
  protected readonly rangeDisabled = computed(() => this.completedCount() === 0);
  protected readonly data = signal<ReportHeader | null>(null);
  protected readonly optionsLoading = signal(false);
  protected readonly headerLoading = signal(false);
  protected readonly errorKey = signal('');
  protected readonly summary = signal<ReportSummary | null>(null);
  protected readonly summaryLoading = signal(false);
  protected readonly summaryError = signal('');
  protected readonly currentScore = computed(() => this.summary()?.iterations.find(row => row.execution_plan_id === this.data()?.current_iteration?.execution_plan_id) ?? null);
  protected readonly comparisonScore = computed(() => this.summary()?.iterations.find(row => row.execution_plan_id === this.data()?.comparison_iteration?.execution_plan_id) ?? null);
  protected readonly loading = computed(() => this.optionsLoading() || this.headerLoading());
  protected readonly selected = computed(() => this.options().find(option => this.key(option) === this.selectedKey()));
  protected readonly rangeInvalid = computed(() => !/^\d+$/.test(this.range()) || +this.range() < 1 || +this.range() > 100);
  protected readonly reportUnavailable = computed(() => {
    const data = this.data();
    return (this.currentScore()?.report_status ?? data?.current_iteration?.report_status) === 'not_available' ||
      (this.comparisonScore()?.report_status ?? data?.comparison_iteration?.report_status) === 'not_available';
  });
  private readonly context = computed(() => {
    const session = this.session.session();
    return JSON.stringify([session?.userCode, session?.schema, session?.role]);
  });
  private optionsRequest?: Subscription;
  private headerRequest?: Subscription;
  private summaryRequest?: Subscription;
  private rangeRequest?: Subscription;
  private comparisonId?: string;

  constructor() {
    effect(() => {
      this.context();
      untracked(() => { this.restoreSelection(); this.loadOptions(); });
    });
  }

  protected navigateTabs(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tab = event.key === 'Home' ? 'aggregate' : event.key === 'End' ? 'locations'
      : this.activeTab() === 'locations' ? 'aggregate' : 'locations';
    this.selectTab(tab);
    (event.currentTarget as HTMLElement).querySelector<HTMLButtonElement>(`#reports-tab-${tab}`)?.focus();
  }

  protected selectTab(tab: 'locations' | 'aggregate'): void {
    this.activeTab.set(tab);
  }

  protected openLocationReport(option: ReportAuditOption): void {
    this.comparisonId = undefined;
    this.range.set('7');
    this.selectedKey.set(this.key(option));
    this.activeTab.set('locations');
    if (this.options().some(row => this.key(row) === this.key(option))) this.loadHeader();
    else this.loadOptions();
    this.host.nativeElement.querySelector<HTMLButtonElement>('#reports-tab-locations')?.focus();
  }

  protected key(option: ReportAuditOption): string {
    return `${option.audit_id}:${option.location_id}`;
  }

  protected loadOptions(): void {
    this.cancelRangeUpdate();
    this.completedCount.set(null);
    this.resetSummary();
    this.optionsRequest?.unsubscribe();
    this.headerRequest?.unsubscribe();
    this.optionsLoading.set(true);
    this.headerLoading.set(false);
    this.errorKey.set('');
    this.data.set(null);
    this.options.set([]);
    this.optionsRequest = this.api.audits().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ results }) => {
        this.options.set(results);
        const option = results.find(row => this.key(row) === this.selectedKey()) ?? results[0];
        if (!option || this.key(option) !== this.selectedKey()) this.comparisonId = undefined;
        this.selectedKey.set(option ? this.key(option) : '');
        this.optionsLoading.set(false);
        if (option) this.loadHeader();
      },
      error: () => { this.optionsLoading.set(false); this.errorKey.set('reports.error.options'); },
    });
  }

  protected selectAudit(key: string): void {
    this.completedCount.set(null);
    this.comparisonId = undefined;
    this.selectedKey.set(key);
    this.loadHeader();
  }

  protected editRange(value: string): string {
    this.cancelRangeUpdate();
    const normalized = /^\d+$/.test(value) && +value > this.rangeMax() ? String(this.rangeMax()) : value;
    this.range.set(normalized);
    this.headerRequest?.unsubscribe();
    this.headerLoading.set(false);
    this.errorKey.set('');
    return normalized;
  }

  protected commitRange(): void {
    if (this.rangeInvalid()) return;
    const option = this.selected(), previous = this.data(), requested = +this.range();
    if (!option || !previous) { this.loadHeader(); return; }
    if (this.rangeLoading() || requested === previous.iterations_requested) return;
    this.cancelRangeUpdate();
    this.summaryRequest?.unsubscribe();
    this.summaryLoading.set(false);
    this.rangeLoading.set(true);
    this.rangeRequest = this.api.header(option, requested).pipe(
      switchMap(header => {
        const normalized = Math.max(1, Math.min(requested, header.total_completed_iterations));
        return header.current_iteration && normalized !== header.iterations_requested
          ? this.api.header(option, normalized) : of(header);
      }),
      switchMap(header => header.current_iteration
        ? this.api.summary(option, header.iterations_requested).pipe(map(summary => ({ header, summary })))
        : of({ header, summary: null })),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: ({ header, summary }) => {
        this.rangeLoading.set(false);
        if (summary && !this.matchesSummary(option, header, summary)) {
          this.rangeUpdateError.set('reports.rangeChanged'); return;
        }
        // Publish the header and scores together so existing charts keep their instances.
        this.completedCount.set(header.total_completed_iterations);
        this.range.set(String(Math.min(header.iterations_requested, this.rangeMax())));
        this.comparisonId = header.comparison_iteration?.execution_plan_id;
        this.data.set(header);
        this.summary.set(summary);
        this.summaryError.set('');
        this.saveSelection();
      },
      error: error => {
        this.rangeLoading.set(false);
        this.rangeUpdateError.set(error?.status === 404 ? 'reports.error.selection' : 'reports.rangeUpdateError');
      },
    });
  }

  private cancelRangeUpdate(): void {
    this.rangeRequest?.unsubscribe();
    this.rangeLoading.set(false);
    this.rangeUpdateError.set('');
  }

  protected retryRange(): void {
    if (this.rangeUpdateError() === 'reports.error.selection') this.loadOptions();
    else this.commitRange();
  }

  protected selectComparison(id: string): void {
    const data = this.data();
    const comparison = data?.comparison_options.find(row => row.execution_plan_id === id);
    if (!data || !comparison) return;
    this.comparisonId = id;
    this.data.set({ ...data, comparison_iteration: comparison });
    this.saveSelection();
  }

  protected loadHeader(): void {
    this.cancelRangeUpdate();
    this.resetSummary();
    this.headerRequest?.unsubscribe();
    this.data.set(null);
    this.errorKey.set('');
    this.headerLoading.set(false);
    const option = this.selected();
    if (!option || this.rangeInvalid()) return;
    this.headerLoading.set(true);
    this.headerRequest = this.api.header(option, +this.range()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => {
        this.completedCount.set(data.total_completed_iterations);
        if (+this.range() > this.rangeMax()) {
          this.range.set(String(this.rangeMax()));
          if (data.current_iteration) {
            this.loadHeader();
            return;
          }
        }
        const comparison = data.comparison_options.find(row => row.execution_plan_id === this.comparisonId)
          ?? data.comparison_iteration;
        this.comparisonId = comparison?.execution_plan_id;
        this.data.set({ ...data, comparison_iteration: comparison });
        this.headerLoading.set(false);
        this.saveSelection();
        if (data.current_iteration) this.loadSummary();
      },
      error: error => {
        this.headerLoading.set(false);
        this.errorKey.set(error?.error?.code === 'report_comparison_not_available' ? 'reports.error.comparison' :
          error?.status === 404 ? 'reports.error.selection' : 'reports.error.header');
      },
    });
  }

  protected retry(): void {
    if (this.errorKey() === 'reports.error.options' || this.errorKey() === 'reports.error.selection') this.loadOptions();
    else this.loadHeader();
  }

  protected loadSummary(): void {
    this.resetSummary();
    const option = this.selected(), header = this.data();
    if (!option || !header?.current_iteration || this.rangeInvalid()) return;
    this.summaryLoading.set(true);
    this.summaryRequest = this.api.summary(option, +this.range()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: summary => {
        this.summaryLoading.set(false);
        // Separate HTTP reads may straddle the completion of a new execution.
        if (!this.matchesSummary(option, header, summary)) {
          this.summaryError.set('reports.score.changed');
          return;
        }
        this.summary.set(summary);
      },
      error: () => { this.summaryLoading.set(false); this.summaryError.set('reports.score.error'); },
    });
  }

  private matchesSummary(option: ReportAuditOption, header: ReportHeader, summary: ReportSummary): boolean {
    const expected = [header.current_iteration!, ...header.comparison_options].map(row => row.execution_plan_id);
    return summary.selection.audit_id === option.audit_id && summary.selection.location_id === option.location_id &&
      summary.current_iteration?.execution_plan_id === header.current_iteration?.execution_plan_id &&
      summary.iterations_requested === header.iterations_requested && summary.iterations.length === expected.length &&
      summary.iterations.every((row, index) => row.execution_plan_id === expected[index]);
  }

  private resetSummary(): void {
    this.summaryRequest?.unsubscribe();
    this.summary.set(null);
    this.summaryLoading.set(false);
    this.summaryError.set('');
  }

  private restoreSelection(): void {
    this.selectedKey.set('');
    this.range.set('7');
    this.comparisonId = undefined;
    try {
      const saved = JSON.parse(sessionStorage.getItem(`junoaudit.reports:${this.context()}`) ?? 'null');
      if (!saved || typeof saved.selection !== 'string' || !Number.isInteger(saved.range) || saved.range < 1 || saved.range > 100) return;
      this.selectedKey.set(saved.selection);
      this.range.set(String(saved.range));
      if (typeof saved.comparison === 'string') this.comparisonId = saved.comparison;
    } catch { /* Storage may be unavailable or contain an obsolete value. */ }
  }

  private saveSelection(): void {
    if (this.rangeInvalid()) return;
    try {
      sessionStorage.setItem(`junoaudit.reports:${this.context()}`, JSON.stringify({
        selection: this.selectedKey(), range: +this.range(), comparison: this.comparisonId,
      }));
    } catch { /* Report selection also works without browser storage. */ }
  }

  protected date(value: string): string {
    return new Intl.DateTimeFormat(this.i18n.language() === 'pl' ? 'pl-PL' : 'en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  }

  protected comparisonLabel(iteration: ReportIteration, index: number): string {
    const position = this.data()!.total_completed_iterations - index - 1;
    return `${this.i18n.t('reports.iteration')} ${position} - ${this.date(iteration.completed_at)}` +
      (iteration.completed_at_is_estimated ? ` · ${this.i18n.t('reports.estimated')}` : '');
  }

  protected selectedComparisonLabel(): string {
    const header = this.data();
    const iteration = header?.comparison_iteration;
    if (!header || !iteration) return '';
    return this.comparisonLabel(iteration, header.comparison_options.findIndex(row => row.execution_plan_id === iteration.execution_plan_id));
  }
}
